import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Page } from "../../../components/page-base";
import useUserStore from "../../../store/user";
import { fonts } from "../../../styles/theme";
import { Card, Meta, cores } from "../../relatorio/styles";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import { ContasConectadas } from "../components/contas-conectadas";
import { LinkDeAcesso } from "../components/link-de-acesso";
import { PEDE_ACESSO, usePedidosDeAcesso, type GerarLink } from "./acesso";
import { CORES_KPI } from "./contas";
import { profileQualityService } from "../services";
import type { ClienteNovo, ContaConectada, EstadoDeAcesso, PerfilQualidade, QualidadePorEmail } from "../types";
import { descricaoDoCliente, situacaoDoCliente } from "./cliente";
import { ESTADOS, desdeQuando, somar } from "./estados";
import { alternarEstado, linhasVisiveis, ordemDaTabela } from "./filtros";

const Conteudo = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 32px 56px;
  box-sizing: border-box;
  background: #f8f9fa;
  font-family: ${fonts.gilroyRegular}, system-ui, sans-serif;
  color: ${cores.ink};
  min-width: 0;

  h2 {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 22px;
    letter-spacing: -0.02em;
    margin: 0;
  }
  @media (max-width: 899px) {
    padding: 20px 16px 48px;
  }
`;

const Situacoes = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const Situacao = styled.button<{ $fundo: string; $cor: string; $ativo: boolean }>`
  border: 2px solid ${({ $ativo, $cor }) => ($ativo ? $cor : "transparent")};
  background: ${({ $fundo }) => $fundo};
  border-radius: 22px;
  padding: 16px 18px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(60, 64, 67, 0.1);
  }
`;

const Tabela = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
    font-size: 14px;
  }
  th {
    text-align: left;
    padding: 12px 14px;
    color: ${cores.ink3};
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 12px;
    border-bottom: 1px solid ${cores.line};
    white-space: nowrap;
  }
  td {
    padding: 12px 14px;
    border-top: 1px solid ${cores.line};
    vertical-align: middle;
  }
  tr:first-child td {
    border-top: 0;
  }
`;

const Contagem = styled.button<{ $cor: string }>`
  border: 0;
  background: none;
  color: ${({ $cor }) => $cor};
  font-family: ${fonts.gilroyBold}, sans-serif;
  font-size: 14px;
  padding: 6px 10px;
  margin: -6px -10px;
  border-radius: 1000px;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  &:hover {
    background: ${cores.grayTint};
  }
`;

const Busca = styled.input`
  min-width: 260px;
  max-width: 420px;
  border: 1px solid ${cores.line};
  border-radius: 1000px;
  padding: 11px 18px;
  font: inherit;
  font-size: 14px;
  outline: none;
  background: #fff;
`;

const Botaozinho = styled.button`
  border: 1px solid ${cores.line};
  background: #fff;
  color: ${cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 13px;
  border-radius: 1000px;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
`;

const ItemDePerfil = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${cores.line};
  font-size: 14px;
  &:first-child {
    border-top: 0;
  }
  a {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
  }
`;

/** Cor de cada situação no mesmo vocabulário dos cartões do topo. */
const TEMA_DA_SITUACAO: Record<EstadoDeAcesso, { fundo: string; cor: string }> = {
  SEM_ACESSO: CORES_KPI.vermelha,
  ACESSO_SOLICITADO: CORES_KPI.azul,
  SUSPENSO: CORES_KPI.amarela,
  COPIA: CORES_KPI.roxa,
  COM_ACESSO: CORES_KPI.verde,
  NAO_VERIFICADO: CORES_KPI.cinza,
};

const ListaDePerfis = ({ perfis, estado, onPedirAcesso, onGerarLink }: { perfis: PerfilQualidade[]; estado: EstadoDeAcesso; onPedirAcesso: (publicId: string) => void; onGerarLink: GerarLink }) => {
  const { pedidoDe, gerar, gerando } = usePedidosDeAcesso(onGerarLink);
  return (
  <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", maxHeight: 520, overflowY: "auto" }}>
    {perfis.map((perfil) => (
      <ItemDePerfil key={perfil.publicId}>
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span>
            <a href={`/dashboard/${perfil.publicId}`} target="_blank" rel="noopener noreferrer">{perfil.name}</a>
            <Meta>{perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}{` · conferido ${desdeQuando(perfil.accessCheckedAt)}`}</Meta>
          </span>
          <Meta>
            {perfil.email ? `Gestor: ${perfil.email}` : "Sem e-mail gestor"}
            {descricaoDoCliente(perfil) ? ` · Cliente: ${descricaoDoCliente(perfil)}` : ""}
            {situacaoDoCliente(perfil.customerStatus) ? ` · ${situacaoDoCliente(perfil.customerStatus)}` : ""}
          </Meta>
        </span>
        {PEDE_ACESSO.includes(estado) && (
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <LinkDeAcesso pedido={pedidoDe(perfil)} gerando={gerando === perfil.publicId} onGerar={() => gerar(perfil.publicId)} />
            {estado === "SEM_ACESSO" && (
              <Botaozinho type="button" onClick={() => onPedirAcesso(perfil.publicId)} style={{ color: cores.ink3 }}>Marcar acesso solicitado</Botaozinho>
            )}
          </span>
        )}
      </ItemDePerfil>
    ))}
  </ul>
  );
};

export const ProfileQuality = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";

  const [linhas, setLinhas] = useState<QualidadePorEmail[]>([]);
  const [filtro, setFiltro] = useState<EstadoDeAcesso | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [contas, setContas] = useState<ContaConectada[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(true);
  const [verificando, setVerificando] = useState<number | "todas" | null>(null);
  const [listaDoEstado, setListaDoEstado] = useState<PerfilQualidade[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [detalhe, setDetalhe] = useState<{ email: string; estado: EstadoDeAcesso; perfis: PerfilQualidade[] } | null>(null);

  const recarregarContas = async () => {
    setCarregandoContas(true);
    setContas(await profileQualityService.getContas(token));
    setCarregandoContas(false);
  };

  const recarregarLinhas = async () => setLinhas(await profileQualityService.getQualidadePorEmail(token));

  useEffect(() => {
    if (!token) return;
    recarregarContas();
    setCarregando(true);
    recarregarLinhas().finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Quem opera precisa ver QUAIS perfis estão na situação, não só quantos:
  // com um estado selecionado, a lista completa (todas as contas) vem junto.
  useEffect(() => {
    if (!token || !filtro) {
      setListaDoEstado([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoLista(true);
    profileQualityService.getPerfis(token, { estado: filtro }).then((lista) => {
      if (!ativo) return;
      setListaDoEstado(lista);
      setCarregandoLista(false);
    });
    return () => {
      ativo = false;
    };
  }, [token, filtro]);

  const verificarConvites = async (userId: number) => {
    setVerificando(userId);
    await profileQualityService.verificarConvites(token, userId);
    await recarregarContas();
    setVerificando(null);
  };

  const verificarTodas = async () => {
    setVerificando("todas");
    await profileQualityService.verificarTodosOsConvites(token);
    setVerificando(null);
  };

  const listarPerfis = (email: string, estado: EstadoDeAcesso) => profileQualityService.getPerfis(token, { email, estado });

  const gerarLink: GerarLink = (publicId) => profileQualityService.criarLinkDeAcesso(token, publicId);

  const clienteNovo = useMemo(
    () => ({
      buscarPlaces: (texto: string) => profileQualityService.buscarNoGoogle(token, texto),
      gerarLink: (cliente: ClienteNovo) => profileQualityService.criarLinkParaClienteNovo(token, cliente),
      listarPedidos: () => profileQualityService.getPedidosDeClientesNovos(token),
    }),
    [token],
  );

  const pedirAcesso = async (publicId: string): Promise<boolean> => {
    const ok = await profileQualityService.marcarAcessoSolicitado(token, publicId);
    if (ok) {
      setListaDoEstado((atual) => atual.filter((perfil) => perfil.publicId !== publicId));
      setDetalhe((atual) => (atual ? { ...atual, perfis: atual.perfis.filter((perfil) => perfil.publicId !== publicId) } : atual));
      await Promise.all([recarregarLinhas(), recarregarContas()]);
    }
    return ok;
  };

  const abrirDetalhe = async (email: string, estado: EstadoDeAcesso) => {
    if (detalhe?.email === email && detalhe.estado === estado) {
      setDetalhe(null);
      return;
    }
    setDetalhe({ email, estado, perfis: [] });
    const perfis = await listarPerfis(email, estado);
    setDetalhe((atual) => (atual?.email === email && atual.estado === estado ? { ...atual, perfis } : atual));
  };

  const clicarNaSituacao = (estado: EstadoDeAcesso) => {
    setFiltro((atual) => alternarEstado(atual, estado));
    setDetalhe(null);
  };

  const visiveis = useMemo(() => ordemDaTabela(linhasVisiveis(linhas, filtro, busca), filtro), [linhas, filtro, busca]);
  const colunas = useMemo(() => (filtro ? ESTADOS.filter((estado) => estado.chave === filtro) : ESTADOS), [filtro]);
  const estadoFiltrado = ESTADOS.find((estado) => estado.chave === filtro);

  return (
    <Page.Root>
      <Conteudo id="dashboard-container">
        <Page.HeaderAux />

        <ContasConectadas
          contas={contas}
          carregando={carregandoContas}
          verificando={verificando}
          onVerificar={verificarConvites}
          onVerificarTodas={verificarTodas}
          onListarPerfis={listarPerfis}
          onPedirAcesso={pedirAcesso}
          onGerarLink={gerarLink}
          clienteNovo={clienteNovo}
        />

        <section aria-label="Perfis por situação de acesso" style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
          <div>
            <h2>Perfis por situação de acesso</h2>
            <Meta>Toque numa situação para ver quais perfis estão nela e em quais contas gestoras; na tabela, cada número abre a lista daquela conta.</Meta>
          </div>

          <Situacoes>
            {ESTADOS.map((estado) => {
              const tema = TEMA_DA_SITUACAO[estado.chave];
              return (
                <Situacao
                  key={estado.chave}
                  type="button"
                  $fundo={tema.fundo}
                  $cor={tema.cor}
                  $ativo={filtro === estado.chave}
                  aria-pressed={filtro === estado.chave}
                  title={estado.explicacao}
                  onClick={() => clicarNaSituacao(estado.chave)}
                >
                  <span style={{ fontFamily: fonts.gilroyBold, fontSize: 30, color: tema.cor, letterSpacing: "-0.03em", lineHeight: 1 }}>
                    <NumeroAnimado valor={somar(linhas, estado.contagem)} />
                  </span>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 13, color: cores.ink2 }}>{estado.rotulo}</span>
                </Situacao>
              );
            })}
          </Situacoes>

          {estadoFiltrado && (
            <Card style={{ borderLeft: `4px solid ${TEMA_DA_SITUACAO[estadoFiltrado.chave].cor}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16 }}>
                  {estadoFiltrado.rotulo}
                  <Meta style={{ marginLeft: 8 }}>{carregandoLista ? "carregando…" : `${listaDoEstado.length.toLocaleString("pt-BR")} ${listaDoEstado.length === 1 ? "perfil" : "perfis"}`}</Meta>
                </span>
                <Meta>{estadoFiltrado.explicacao}</Meta>
              </div>
              {!carregandoLista && listaDoEstado.length === 0 && <Meta style={{ display: "block", marginTop: 12 }}>Nenhum perfil nesta situação.</Meta>}
              <ListaDePerfis perfis={listaDoEstado} estado={estadoFiltrado.chave} onPedirAcesso={pedirAcesso} onGerarLink={gerarLink} />
            </Card>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Busca value={busca} onChange={(evento) => setBusca(evento.target.value)} placeholder="Filtrar contas por e-mail" aria-label="Filtrar contas por e-mail" />
            {estadoFiltrado && (
              <Botaozinho type="button" onClick={() => clicarNaSituacao(estadoFiltrado.chave)}>{`Limpar filtro · ${visiveis.length} ${visiveis.length === 1 ? "conta" : "contas"}`}</Botaozinho>
            )}
          </div>

          {carregando ? (
            <Meta>Carregando…</Meta>
          ) : visiveis.length === 0 ? (
            <Card><Meta>Nenhuma conta nesta situação.</Meta></Card>
          ) : (
            <Card style={{ padding: "6px 10px" }}>
              <Tabela>
                <table>
                  <thead>
                    <tr>
                      <th>E-mail gestor</th>
                      <th>Perfis</th>
                      {colunas.map((estado) => (
                        <th key={estado.chave} title={estado.explicacao}>{estado.rotulo}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visiveis.map((linha) => (
                      <tr key={linha.email}>
                        <td style={{ fontFamily: fonts.gilroySemiBold, overflowWrap: "anywhere" }}>{linha.email}</td>
                        <td><Meta>{linha.total.toLocaleString("pt-BR")}</Meta></td>
                        {colunas.map((estado) => {
                          const quantos = estado.contagem(linha);
                          const aberto = detalhe?.email === linha.email && detalhe.estado === estado.chave;
                          return (
                            <td key={estado.chave}>
                              {quantos > 0 ? (
                                <Contagem type="button" $cor={TEMA_DA_SITUACAO[estado.chave].cor} aria-pressed={aberto} onClick={() => abrirDetalhe(linha.email, estado.chave)} style={aberto ? { background: TEMA_DA_SITUACAO[estado.chave].fundo } : undefined}>
                                  {quantos.toLocaleString("pt-BR")}
                                </Contagem>
                              ) : (
                                <span style={{ color: "#cbd3dc" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tabela>
            </Card>
          )}

          {detalhe && (
            <Card style={{ borderLeft: `4px solid ${TEMA_DA_SITUACAO[detalhe.estado].cor}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16 }}>{ESTADOS.find((estado) => estado.chave === detalhe.estado)?.rotulo}</span>
                  <Meta style={{ display: "block", marginTop: 2 }}>{`${detalhe.email} · ${detalhe.perfis.length} ${detalhe.perfis.length === 1 ? "perfil" : "perfis"}`}</Meta>
                </div>
                <Botaozinho type="button" onClick={() => setDetalhe(null)} aria-label="Fechar lista de perfis">Fechar</Botaozinho>
              </div>
              <ListaDePerfis perfis={detalhe.perfis} estado={detalhe.estado} onPedirAcesso={pedirAcesso} onGerarLink={gerarLink} />
            </Card>
          )}
        </section>
      </Conteudo>
    </Page.Root>
  );
};
